import React from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Stack,
  Typography,
  InputAdornment,
  Grid,
  InputBase,
  IconButton,
  Pagination,
  Tooltip
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import type { SkuCostConfig } from "@/features/profit-calculators/calculator-shared/types"
import {
  calculateSkuCost,
  useSkuCostEditor
} from "@/features/profit-calculators/calculator-shared/hooks/useSkuCostEditor"

interface SkuCostModalProps {
  open: boolean
  onClose: () => void
  uniqueSkus: { sku: string; productName: string }[]
  initialCosts: Record<string, SkuCostConfig>
  onSave: (costs: Record<string, SkuCostConfig>) => void
}

const SkuCostModal: React.FC<SkuCostModalProps> = ({ open, onClose, uniqueSkus, initialCosts, onSave }) => {
  const {
    costs,
    search,
    setSearch,
    selectedSkus,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    bulkProductPrice,
    setBulkProductPrice,
    bulkProductGst,
    setBulkProductGst,
    bulkPackingPrice,
    setBulkPackingPrice,
    bulkPackingGst,
    setBulkPackingGst,
    bulkOtherPrice,
    setBulkOtherPrice,
    filteredSkus,
    paginatedSkus,
    totalPages,
    updateCost: handleFieldChange,
    toggleSku: handleSelectRow,
    selectAll: handleSelectAll,
    applyBulkEdit: handleApplyBulkEdit
  } = useSkuCostEditor(open, uniqueSkus, initialCosts)

  const handleSave = () => {
    onSave(costs)
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle sx={{ pb: 1, borderBottom: 1, borderColor: "divider" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={750}>
            Configure SKU Cost &amp; GST Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {uniqueSkus.length} Unique SKUs Detected
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: "68vh", display: "flex", flexDirection: "column" }}>
        {/* Bulk Edit Panel - Direct at the top */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            m: 2,
            mb: 1,
            borderRadius: 3,
            bgcolor: theme => (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)")
          }}>
          <Typography variant="body2" fontWeight={700} color="primary.main" sx={{ mb: 1.5 }}>
            BULK EDIT CHECKED PRODUCTS
          </Typography>
          <Grid container spacing={1.5} alignItems="center">
            {/* Product Price */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Product Price"
                value={bulkProductPrice}
                onChange={e => setBulkProductPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* Product GST */}
            <Grid item xs={6} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Prod. GST"
                value={bulkProductGst}
                onChange={e => setBulkProductGst(e.target.value)}
                inputProps={{ min: 0, step: "any" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* Packing Price */}
            <Grid item xs={6} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Packing Cost"
                value={bulkPackingPrice}
                onChange={e => setBulkPackingPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* Packing GST */}
            <Grid item xs={6} sm={1.5}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Pack. GST"
                value={bulkPackingGst}
                onChange={e => setBulkPackingGst(e.target.value)}
                inputProps={{ min: 0, step: "any" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">%</InputAdornment>
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* Other Cost */}
            <Grid item xs={12} sm={2}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label="Other Cost"
                value={bulkOtherPrice}
                onChange={e => setBulkOtherPrice(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              />
            </Grid>

            {/* Action button */}
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApplyBulkEdit}
                disabled={
                  selectedSkus.size === 0 ||
                  (bulkProductPrice === "" &&
                    bulkProductGst === "" &&
                    bulkPackingPrice === "" &&
                    bulkPackingGst === "" &&
                    bulkOtherPrice === "")
                }
                sx={{
                  borderRadius: 2,
                  height: 40,
                  textTransform: "none",
                  fontWeight: 700
                }}>
                Apply to Checked ({selectedSkus.size})
              </Button>
            </Grid>
          </Grid>
          {selectedSkus.size === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              * Check one or more SKUs in the table below to apply these bulk rates.
            </Typography>
          )}
        </Paper>

        {/* Search Toolbar */}
        <Stack direction="row" sx={{ px: 2, py: 1 }} alignItems="center" justifyContent="space-between">
          <Paper
            variant="outlined"
            sx={{
              p: "2px 4px",
              display: "flex",
              alignItems: "center",
              width: 320,
              borderRadius: 2
            }}>
            <IconButton sx={{ p: "8px" }} disabled>
              <SearchIcon />
            </IconButton>
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search SKU or Product Name..."
              value={search}
              onChange={e => {
                setSearch(e.target.value)
                setPage(1) // Reset page on filter change
              }}
            />
          </Paper>
        </Stack>

        {/* Table list */}
        <TableContainer sx={{ flex: 1, overflowY: "auto", px: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow
                sx={{
                  "& th": { fontWeight: 700, bgcolor: "background.paper" }
                }}>
                <TableCell width={50} align="center">
                  <Checkbox
                    indeterminate={selectedSkus.size > 0 && selectedSkus.size < filteredSkus.length}
                    checked={filteredSkus.length > 0 && selectedSkus.size === filteredSkus.length}
                    onChange={e => handleSelectAll(e.target.checked)}
                  />
                </TableCell>
                <TableCell width={300}>Product Details</TableCell>
                <TableCell align="right" width={110}>
                  Prod. Price (₹)
                </TableCell>
                <TableCell align="center" width={110}>
                  Prod. GST (%)
                </TableCell>
                <TableCell align="right" width={110}>
                  Packing (₹)
                </TableCell>
                <TableCell align="center" width={110}>
                  Pack. GST (%)
                </TableCell>
                <TableCell align="right" width={110}>
                  Other (₹)
                </TableCell>
                <TableCell align="right" width={120}>
                  Landed Cost (₹)
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSkus.map(s => {
                const item = costs[s.sku]
                const isSelected = selectedSkus.has(s.sku)
                if (!item) return null

                return (
                  <TableRow key={s.sku} hover selected={isSelected} sx={{ "&:last-child td": { border: 0 } }}>
                    <TableCell align="center">
                      <Checkbox checked={isSelected} onChange={() => handleSelectRow(s.sku)} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={s.productName} placement="top">
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 280, cursor: "pointer" }}>
                          {s.productName}
                        </Typography>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        SKU: {s.sku}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.productPrice}
                        onChange={e => handleFieldChange(s.sku, "productPrice", parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={item.productGst}
                        onChange={e => handleFieldChange(s.sku, "productGst", parseFloat(e.target.value) || 0)}
                        inputProps={{
                          min: 0,
                          step: "any",
                          style: { textAlign: "right" }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                          width: 85
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.packingPrice}
                        onChange={e => handleFieldChange(s.sku, "packingPrice", parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        size="small"
                        type="number"
                        value={item.packingGst}
                        onChange={e => handleFieldChange(s.sku, "packingGst", parseFloat(e.target.value) || 0)}
                        inputProps={{
                          min: 0,
                          step: "any",
                          style: { textAlign: "right" }
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                          width: 85
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={item.otherPrice}
                        onChange={e => handleFieldChange(s.sku, "otherPrice", parseFloat(e.target.value) || 0)}
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                        sx={{
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={700} color="primary.main">
                        ₹{calculateSkuCost(item).toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              })}

              {paginatedSkus.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No SKUs found matching search filter.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Table Pagination Controls */}
        {totalPages > 1 && (
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Rows per page:
              </Typography>
              <Select
                size="small"
                value={rowsPerPage}
                onChange={e => {
                  setRowsPerPage(Number(e.target.value))
                  setPage(1)
                }}
                sx={{
                  borderRadius: 1.5,
                  height: 30,
                  "& .MuiSelect-select": { py: 0.5 }
                }}>
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </Select>
            </Stack>
            <Pagination count={totalPages} page={page} onChange={(_, p) => setPage(p)} color="primary" size="small" />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          startIcon={<CheckCircleIcon />}
          sx={{ borderRadius: 2 }}>
          Save &amp; Proceed
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SkuCostModal
